import React, { useMemo, useState } from "react";

export interface RdmConfigRow {
  id: number;
  config_name: string;
  created_at: string;
}

export interface RdmTableStyles {
  tableStyle: React.CSSProperties;
  headerStyle: React.CSSProperties;
  lastHeaderStyle: React.CSSProperties;
  cellStyle: React.CSSProperties;
  lastCellStyle: React.CSSProperties;
  filterRowStyle: React.CSSProperties;
  filterInputStyle: React.CSSProperties;
  rowStyle: React.CSSProperties;
  rowHoverColor: string;
}

interface RdmTableProps {
  data: RdmConfigRow[];
  styles: RdmTableStyles;
  selectedId: number | null;
  onRowClick?: (row: RdmConfigRow) => void;
  onRowDoubleClick?: (row: RdmConfigRow) => void;
}

type SortField = keyof RdmConfigRow;
type SortDirection = "asc" | "desc";

export const RdmTable: React.FC<RdmTableProps> = ({
  data,
  styles,
  selectedId,
  onRowClick,
  onRowDoubleClick,
}) => {
  const [sortField, setSortField] = useState<SortField>("id");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [filters, setFilters] = useState<{
    id: string;
    config_name: string;
    created_at: string;
  }>({ id: "", config_name: "", created_at: "" });

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return " ↕";
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleFilterChange = (field: keyof typeof filters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const filteredAndSorted = useMemo(() => {
    const filtered = data.filter((row) => {
      return (
        row.id.toString().includes(filters.id) &&
        row.config_name
          .toLowerCase()
          .includes(filters.config_name.toLowerCase()) &&
        row.created_at.toLowerCase().includes(filters.created_at.toLowerCase())
      );
    });

    return filtered.sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.localeCompare(bValue);
        return sortDirection === "asc" ? comparison : -comparison;
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        const comparison = aValue - bValue;
        return sortDirection === "asc" ? comparison : -comparison;
      }
      return 0;
    });
  }, [data, filters, sortField, sortDirection]);

  return (
    <div style={{ overflowX: "auto", marginTop: "20px" }}>
      <table style={styles.tableStyle}>
        <thead>
          <tr>
            <th style={styles.headerStyle} onClick={() => handleSort("id")}>
              ID{getSortIcon("id")}
            </th>
            <th
              style={styles.headerStyle}
              onClick={() => handleSort("config_name")}
            >
              Config Name{getSortIcon("config_name")}
            </th>
            <th
              style={styles.lastHeaderStyle}
              onClick={() => handleSort("created_at")}
            >
              Created At{getSortIcon("created_at")}
            </th>
          </tr>
          <tr style={styles.filterRowStyle}>
            <td style={styles.cellStyle}>
              <input
                type="text"
                placeholder="Filter ID..."
                value={filters.id}
                onChange={(e) => handleFilterChange("id", e.target.value)}
                style={styles.filterInputStyle}
              />
            </td>
            <td style={styles.cellStyle}>
              <input
                type="text"
                placeholder="Filter name..."
                value={filters.config_name}
                onChange={(e) =>
                  handleFilterChange("config_name", e.target.value)
                }
                style={styles.filterInputStyle}
              />
            </td>
            <td style={styles.lastCellStyle}>
              <input
                type="text"
                placeholder="Filter date..."
                value={filters.created_at}
                onChange={(e) =>
                  handleFilterChange("created_at", e.target.value)
                }
                style={styles.filterInputStyle}
              />
            </td>
          </tr>
        </thead>
        <tbody>
          {filteredAndSorted.map((row) => (
            <tr
              key={row.id}
              style={{
                ...styles.rowStyle,
                backgroundColor:
                  selectedId === row.id ? styles.rowHoverColor : "transparent",
              }}
              onClick={() => onRowClick && onRowClick(row)}
              onDoubleClick={() => onRowDoubleClick && onRowDoubleClick(row)}
              onMouseEnter={(e) => {
                if (selectedId !== row.id) {
                  e.currentTarget.style.backgroundColor = styles.rowHoverColor;
                }
              }}
              onMouseLeave={(e) => {
                if (selectedId !== row.id) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              <td style={styles.cellStyle}>{row.id}</td>
              <td style={styles.cellStyle}>{row.config_name}</td>
              <td style={styles.lastCellStyle}>{row.created_at}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RdmTable;
